import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { MdDelete } from "react-icons/md";
import {
  FiEdit,
  FiEye,
  FiMoreVertical,
  FiCheck,
  FiX,
  FiRotateCcw,
  FiDollarSign,
  FiDownload,
  FiKey,
} from "react-icons/fi";
import Table from "./Table";
import {
  getStatusColor,
  DESIGN_COLORS,
} from "../../../utilities/statusColorMap";

// Design system for table styling
const DESIGN = {
  colors: {
    text: {
      primary: "#1a1a1a",
      secondary: "#666666",
      muted: "#999999",
      rowNumber: "#2c3e50",
    },
    status: DESIGN_COLORS.status,
    link: "#0066cc",
    actionIcon: "#6b7280",
  },
  typography: {
    header: { fontWeight: "500", fontSize: "12px", letterSpacing: "0px" },
    body: { fontWeight: "500", fontSize: "12px" },
    data: { fontWeight: "500", fontSize: "12px" },
    rowNumber: { fontWeight: "600", fontSize: "13px" },
  },
  spacing: {
    cell: { padding: "10px 9px" },
  },
};

const TableWrapper = ({
  columns,
  data,
  actions = {},
  options = {},
  isCardLayout = false,
}) => {
  const { i18n, t } = useTranslation();

  const {
    onView,
    onEdit,
    onDelete,
    onConfirm,
    onReject,
    onVerificationEdit,
    onUndo,
    onAlwaysEdit,
    onPay,
    onDownloadPdf,
    onChangePassword,
  } = actions;

  const {
    showActions = true,
    actionIcons = ["view"],
    page = 1,
    hideRowNumber = true,
  } = options;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString.split("T")[0]);
      const locale = i18n.language === "ar" ? "ar-SA" : "en-US";
      return date.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const renderCellValue = (value, row, column) => {
    if (column.render) {
      return column.render(value, row);
    }

    if (column.isDate && value) {
      return <span style={DESIGN.typography.data}>{formatDate(value)}</span>;
    }

    if (column.isDateRange && value) {
      const [startDate, endDate] = value.split(" - ");
      return (
        <span style={DESIGN.typography.data}>
          {formatDate(startDate)} - {formatDate(endDate)}
        </span>
      );
    }

    if (column.isLink) {
      return (
        <ChakraLink
          color={DESIGN.colors.link}
          textDecoration="underline"
          _hover={{ textDecoration: "underline" }}
          {...DESIGN.typography.body}
        >
          {value}
        </ChakraLink>
      );
    }

    if (column.isStatus) {
      return (
        <span
          style={{
            ...DESIGN.typography.data,
            color: getStatusColor(value.toLowerCase()),
          }}
        >
          {value.toLowerCase()}
        </span>
      );
    }

    return (
      <span
        style={{ ...DESIGN.typography.data, color: DESIGN.colors.text.primary }}
      >
        {value || "-"}
      </span>
    );
  };

  const isRTL = i18n.language === "ar";

  const styles = `
    .table-wrapper-responsive {
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
      -webkit-appearance: none !important;
    }

    .table-wrapper-responsive::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    .table-wrapper-responsive table {
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }

    .table-wrapper-responsive table::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    ${
      isCardLayout
        ? `
      .table-wrapper-responsive table {
        width: 100%;
        display: block;
        position: relative;
      }

      .table-wrapper-responsive thead {
        display: none;
      }

      .table-wrapper-responsive tbody {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 0;
      }

      .table-wrapper-responsive tr {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 20px 16px;
        padding: 16px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #ffffff;
        position: relative;
      }

      .table-wrapper-responsive td {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 0 !important;
      }

      .table-wrapper-responsive td::before {
        content: attr(data-label);
        font-weight: 600;
        font-size: 11px;
        color: ${DESIGN.colors.text.secondary};
        text-transform: uppercase;
        letter-spacing: 0.5px;
        line-height: 1.2;
      }

      .table-wrapper-responsive td[data-label=""]::before {
        display: none;
      }

      .table-wrapper-responsive .row-actions {
        position: absolute;
        top: 12px;
        ${isRTL ? "left" : "right"}: 12px;
        padding: 0;
        display: block;
      }

      .table-wrapper-responsive td span {
        font-weight: 500;
        font-size: 13px;
        color: ${DESIGN.colors.text.primary};
        word-break: break-word;
      }
    `
        : `
      .table-wrapper-responsive td.row-actions {
        display: table-cell !important;
        position: static;
        text-align: center;
        width: auto;
        padding: ${DESIGN.spacing.cell.padding} !important;
      }
    `
    }
  `;

  const ActionMenu = ({ row, rowActionIcons }) => (
    <Menu>
      <MenuButton
        as={Button}
        bg="transparent"
        size="sm"
        p={1}
        minW="auto"
        _hover={{ bg: "rgba(0, 0, 0, 0.04)" }}
        _active={{ bg: "rgba(0, 0, 0, 0.04)" }}
      >
        <Icon
          as={FiMoreVertical}
          boxSize={5}
          color={DESIGN.colors.actionIcon}
        />
      </MenuButton>
      <MenuList minW="160px">
        {rowActionIcons.includes("view") && onView && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onView(row)}
            textTransform={"capitalize"}
            icon={<Icon as={FiEye} boxSize={4} />}
          >
            {t("general.view")}
          </MenuItem>
        )}
        {rowActionIcons.includes("edit") && onEdit && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onEdit(row)}
            icon={<Icon as={FiEdit} boxSize={4} />}
            isDisabled={row.status !== "PENDING"}
          >
            {t("general.edit")}
          </MenuItem>
        )}
        {rowActionIcons.includes("alwaysEdit") && onAlwaysEdit && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onAlwaysEdit(row)}
            icon={<Icon as={FiEdit} boxSize={4} />}
          >
            {t("general.edit")}
          </MenuItem>
        )}
        {rowActionIcons.includes("delete") && onDelete && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onDelete(row)}
            icon={<Icon as={MdDelete} boxSize={4} />}
            color={DESIGN.colors.status.error}
          >
            {t("general.delete")}
          </MenuItem>
        )}
        {rowActionIcons.includes("confirm") && onConfirm && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onConfirm(row)}
            icon={<Icon as={FiCheck} boxSize={4} />}
            color={DESIGN.colors.status.success}
          >
            {t("general.confirm")}
          </MenuItem>
        )}
        {rowActionIcons.includes("reject") && onReject && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onReject(row)}
            icon={<Icon as={FiX} boxSize={4} />}
            color={DESIGN.colors.status.error}
          >
            {t("general.reject")}
          </MenuItem>
        )}
        {rowActionIcons.includes("verificationEdit") && onVerificationEdit && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onVerificationEdit(row)}
            icon={<Icon as={FiEdit} boxSize={4} />}
          >
            {t("general.edit")}
          </MenuItem>
        )}
        {rowActionIcons.includes("undo") && onUndo && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onUndo(row)}
            icon={<Icon as={FiRotateCcw} boxSize={4} />}
            color={DESIGN.colors.status.warning}
          >
            {t("pages.reel_reports.undo")}
          </MenuItem>
        )}
        {rowActionIcons.includes("pay") && onPay && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onPay(row)}
            icon={<Icon as={FiDollarSign} boxSize={4} />}
            color={DESIGN.colors.status.success}
          >
            {t("general.pay")}
          </MenuItem>
        )}
        {rowActionIcons.includes("changePassword") && onChangePassword && (
          <MenuItem
            fontSize="14px"
            fontWeight="500"
            onClick={() => onChangePassword(row)}
            icon={<Icon as={FiKey} boxSize={4} />}
          >
            {t("general.change_password")}
          </MenuItem>
        )}
        {rowActionIcons.includes("downloadPdf") &&
          (row.userPdf || row.ownerPdf) && (
            <>
              {row.userPdf && (
                <MenuItem
                  fontSize="14px"
                  fontWeight="500"
                  onClick={() => window.open(row.userPdf, "_blank")}
                  icon={<Icon as={FiDownload} boxSize={4} />}
                  color={DESIGN.colors.link}
                >
                  {t("pages.invoices.user_invoice") || t("general.download")}
                </MenuItem>
              )}
              {row.ownerPdf && (
                <MenuItem
                  fontSize="14px"
                  fontWeight="500"
                  onClick={() => window.open(row.ownerPdf, "_blank")}
                  icon={<Icon as={FiDownload} boxSize={4} />}
                  color={DESIGN.colors.link}
                >
                  {t("pages.invoices.owner_invoice") || t("general.download")}
                </MenuItem>
              )}
            </>
          )}
      </MenuList>
    </Menu>
  );

  return (
    <>
      <style>{styles}</style>
      <div
        style={{ width: "100%", overflow: "hidden" }}
        className="table-wrapper-responsive"
      >
        <Table
          style={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              {!hideRowNumber && (
                <th
                  style={{
                    ...DESIGN.typography.header,
                    ...DESIGN.spacing.cell,
                    color: DESIGN.colors.text.secondary,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  #
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    ...DESIGN.typography.header,
                    ...DESIGN.spacing.cell,
                    color: DESIGN.colors.text.secondary,
                    whiteSpace: "normal",
                    wordBreak: "normal",
                    lineHeight: "1",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {column.label}
                </th>
              ))}
              {showActions && (
                <th
                  style={{
                    ...DESIGN.typography.header,
                    ...DESIGN.spacing.cell,
                    color: DESIGN.colors.text.secondary,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {t("general.action")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, index) => {
                const rowActionIcons =
                  typeof actionIcons === "function"
                    ? actionIcons(row)
                    : actionIcons;
                return (
                  <tr key={row._id || index}>
                    {!hideRowNumber && (
                      <td
                        data-label="#"
                        style={{
                          ...DESIGN.typography.rowNumber,
                          ...DESIGN.spacing.cell,
                          color: DESIGN.colors.text.rowNumber,
                          width: "auto",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {String((page - 1) * 10 + (index + 1)).padStart(3, "0")}
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={`${row._id}-${column.key}`}
                        data-label={column.label}
                        style={{
                          ...DESIGN.spacing.cell,
                          ...(column.key === "email" ? { textTransform: "lowercase" } : {}),
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {renderCellValue(row[column.key], row, column)}
                      </td>
                    ))}
                    {showActions && (rowActionIcons.length > 0 || true) && (
                      <td
                        className="row-actions"
                        style={{
                          ...DESIGN.spacing.cell,
                          textAlign: "center",
                          width: "30px",
                        }}
                        data-label=""
                      >
                        {rowActionIcons.length > 0 && (
                          <ActionMenu
                            row={row}
                            rowActionIcons={rowActionIcons}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length + (showActions ? 2 : 1)}
                  style={{
                    textAlign: "center",
                    padding: "32px 16px",
                    color: DESIGN.colors.text.muted,
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  {t("general.no_data") || "No data available"}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default TableWrapper;
